import { useQuery } from "@tanstack/react-query";
import { AccountInfo, AccountsResponse } from "@/app/api/v1/accounts/utils";
import { sanitizeErrorMessage } from "@/app/schedules/[tn]/ErrorMessageSanitization";
import { AuthProvider } from "@/auth-types";
import { VerificationState, VFCCSession } from "@/stores/VFCCSessionStore";
import { LoggerObject, sanitizeLogMessage } from "@/utils/logger";
import { retryLogic } from "@/utils/network";

/**
 * This is a react-query hook that retrieves all TNs user has access to
 * @param provider - The provider to verify if user is Halo B
 * @return The TN list for the user.
 */
export function useAccountsQuery(provider: AuthProvider | undefined, vfccSession: VFCCSession) {
    const loggerObj = new LoggerObject("useAccountsQuery", undefined);
    return useQuery({
        queryKey: ["accounts", provider],
        queryFn: () => getAccounts(loggerObj),
        retry: retryLogic,
        enabled: provider === AuthProvider.HaloB && vfccSession.state === VerificationState.UNVERIFIED, // Prevent API call if provider isn't HaloB
        staleTime: 30 * 60 * 1000, // Refresh every 30 minutes
    });
}

/**
 * This function is used to get list of TNs user has access to
 * @return The list of Accounts the user has access to
 */
export async function getAccounts(parentLoggerObj: LoggerObject): Promise<AccountsResponse> {
    const loggerObj = parentLoggerObj.createChildLogger("getAccounts");
    loggerObj.getLogger().debug("Entered Function");

    const res: Response = await fetch(`/api/v1/accounts`);
    if (!res.ok) {
        const err: string = await res.text();
        loggerObj.getLogger().debug(JSON.stringify(sanitizeLogMessage(err)));
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}

/**
 * This is a react-query hook that retrieves all TNs user has access to
 * @param provider - The provider to verify if user is Halo E
 * @param ubid - The ub sub account provided by user
 * @return The TN list for the user.
 */
export function useAccountQuery(provider: AuthProvider | undefined, ubid: string, vfccSession: VFCCSession) {
    const loggerObj = new LoggerObject("useAccountQuery", undefined);
    return useQuery({
        queryKey: ["account", provider, ubid],
        queryFn: () => getAccount(ubid, loggerObj),
        retry: retryLogic,
        enabled: provider === AuthProvider.HaloE && !!ubid && (vfccSession.state === VerificationState.UNVERIFIED || vfccSession.ubid !== ubid), // Prevent API call if provider isn't HaloE
        staleTime: 30 * 60 * 1000, // Refresh every 30 minutes
    });
}

/**
 * This function is used to get list of TNs user has access to
 * @param ubid - The ub sub account provided by user
 * @return The account with a list of TNs the user has access to
 */
export async function getAccount(ubid: string, parentLoggerObj: LoggerObject): Promise<AccountInfo> {
    const loggerObj = parentLoggerObj.createChildLogger("getAccount");
    loggerObj.getLogger().debug("Entered Function");
    const res: Response = await fetch(`/api/v1/accounts/${ubid}`);
    if (!res.ok) {
        const err: string = await res.text();
        loggerObj.getLogger().debug(JSON.stringify(sanitizeLogMessage(err)));
        throw new Error(sanitizeErrorMessage(err), { cause: res.status });
    }
    return res.json();
}
